// -------------------------------
// 🔹 Imports externes (librairies tierces)
// -------------------------------
import { useMutation } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';

// -------------------------------
// 🔹 Imports internes — Références réactives (refs globales)
// -------------------------------
import { isGlobalError, GlobalErrorMessage } from '@/refs/common.refs';
import { isLoaderLoginFormActivated } from '@/refs/login.form.refs';

// -------------------------------
// 🔹 Imports internes — Stores Pinia
// -------------------------------
import { useCommerceStore } from '@/store/commerce.store';
import { useCategoryStore } from '@/store/category.store';
import { useDialogStore } from '@/store/dialog.store';

// -------------------------------
// 🔹 Imports internes — Types TypeScript
// -------------------------------
import type { ICategoryBody } from '@/types/category.types';

/**
 * Hook pour gérer les mutations liées aux catégories.
 * Fournit des fonctions pour créer, mettre à jour et supprimer des catégories
 * avec gestion automatique des erreurs globales et du loader.
 */
export const CategorieMutation = () => {
  const dialogStore = useDialogStore();
  const categorieStore = useCategoryStore();

  // -------------------------------
  // 🔹 Création d'une catégorie
  // -------------------------------
  const createCategorie = useMutation<any, Error, ICategoryBody>({
    mutationFn: (payload: ICategoryBody) => {
      return categorieStore.createCategory(payload);
    },
    onSuccess: async (data) => {
      // Ici, tu peux mettre à jour le store, fermer un dialogue, ou afficher un message de succès
    },
    onError: (err) => {
      const error = err as AxiosError;
      const response = error.response?.data as any;

      const message = response?.message[0];
      isGlobalError.value = true;
      GlobalErrorMessage.value = message;
      setTimeout(() => {
        isGlobalError.value = false;
        isLoaderLoginFormActivated.value = false;
      }, 1000);
    },
  });

  // -------------------------------
  // 🔹 Mise à jour d'une catégorie par ID
  // -------------------------------
  const updateCategoryById = useMutation<any, Error, ICategoryBody>({
    mutationFn: (payload: ICategoryBody) => {
      return categorieStore.updateCategoryById(payload);
    },
    onSuccess: async (data) => {
      // Mettre à jour le store ou notifier l'utilisateur
    },
    onError: (err) => {
      const error = err as AxiosError;
      const response = error.response?.data as any;

      const message = response?.message[0];
      isGlobalError.value = true;
      GlobalErrorMessage.value = message;
      setTimeout(() => {
        isGlobalError.value = false;
        isLoaderLoginFormActivated.value = false;
      }, 1000);
    },
  });

  // -------------------------------
  // 🔹 Suppression d'une catégorie
  // -------------------------------
  const DeleteCategorie = useMutation<any, Error, any>({
    mutationFn: () => {
      return categorieStore.DeleteCategorie();
    },
    onSuccess: async (data) => {
      isLoaderLoginFormActivated.value = true;
      dialogStore.visible = false;

      setTimeout(() => {
        isLoaderLoginFormActivated.value = false;
      }, 1000);
    },
    onError: (err) => {
      const error = err as AxiosError;
      const response = error.response?.data as any;

      const message = response?.message[0];
      isGlobalError.value = true;
      GlobalErrorMessage.value = message;
      setTimeout(() => {
        isGlobalError.value = false;
        isLoaderLoginFormActivated.value = false;
      }, 1000);
    },
  });

  // -------------------------------
  // 🔹 Fonctions exposées pour les composants
  // -------------------------------
  /**
   * Crée une nouvelle catégorie
   * @param {ICategoryBody} item - Données de la catégorie
   */
  const handleCreateCategory = (item: ICategoryBody) => {
    return createCategorie.mutateAsync(item);
  };

  /**
   * Supprime une catégorie existante
   * @param {any} items - Optionnel, données pour identifier la catégorie à supprimer
   */
  const handleDeleteCategorie = (items?: any) => {
    DeleteCategorie.mutate(items);
  };

  /**
   * Met à jour une catégorie existante
   * @param {ICategoryBody} item - Données de mise à jour de la catégorie
   */
  const handleUpdateCategoryById = (item: ICategoryBody) => {
    return updateCategoryById.mutateAsync(item);
  };

  return {
    handleCreateCategory,
    handleDeleteCategorie,
    handleUpdateCategoryById,
  };
};
